/**
 * +quests: side quests in a world session (docs/design/zones.md "Side quests"). WorldApp owns one
 * and forwards its map loads, upkeep, field gains and NPC interacts here:
 *
 *  - talking to a quest NPC opens the quest window (hud/quests/questDialog.ts): Accept / Complete go
 *    to the server as `questAccept` / `questTurnIn` (the turn-in rolls a gear reward when it has one);
 *  - over-head markers ('!' offers, '?' hand-ins, grey '!' unlocks later, grey '?' in progress), the
 *    tracker rows under the main quest, and the waypoint arrow for the tracked side quest (or the
 *    first one when the main quest points nowhere);
 *  - kill progress comes back on `fieldKills` (FieldGain.quests): collected items pop from the hero,
 *    "Damp Socks: 3/5" flashes mid-screen, a finished objective chimes;
 *  - barks: an NPC calls out a line (a chat-style bubble and a little hop) when the hero walks past;
 *  - the quest log (Y): track and abandon.
 */
import type { FieldNpcDef, UnitId, Vec2 } from '@keystone/sim';
import { isQuestNpc, questNpcVoice, sideQuestById, sideQuestTurnInId } from '@keystone/content';
import { type FieldGain, type Item, QUEST_LOG_MAX, sideQuestsOf } from '@keystone/progression';
import type { Session } from '../game/session';
import type { ProfileStore } from '../meta/profileStore';
import { playSound } from '../audio/engine';
import { kmToast } from '../meta/kmmo/components';
import { celebrations } from '../hud/field/celebrate';
import { rewardPops } from '../hud/field/rewardPops';
import { closeTownPanel, townPanelOpen, type TownPanel } from '../hud/field/townPanel';
import { el } from '../hud/dom';
import { openQuestDialog } from '../hud/quests/questDialog';
import { openQuestLog } from '../hud/quests/questLog';
import { BarkBubbles } from '../hud/quests/barks';
import type { NpcMarker } from '../render/types';
import type { TrackerRow, Waypoint } from './fieldView';
import { type QuestNames, firstActiveQuest, npcQuestMenu, questNpcMarkers, questProgressText, sideQuestRows, sideQuestWaypoint } from './sideQuestModel';

/** A quest NPC calls out when the hero comes this close (and is not already talking to it). */
export const BARK_YD = 11;
/** The same NPC barks again at most this often, and any NPC at most every BARK_GAP_MS. */
export const BARK_COOLDOWN_MS = 28_000;
const BARK_GAP_MS = 3_500;
/** Bubble height above the NPC's feet (over its name and marker). */
const BARK_HEIGHT = 5.1;

export interface SideQuestHost {
  store: ProfileStore;
  hudHost: HTMLElement;
  names(): QuestNames & { npc(id: string): string | undefined };
  /** XP / level-up / quest results of a field intent (WorldApp.applyGain). */
  applyGain(g: FieldGain): void;
  /** Re-read the profile into the HUD (tracker, markers, waypoint). */
  refresh(): void;
  /** Save the hero's position now (the server checks "next to the NPC" on it). */
  savePosition(): void;
  heroLevel(): number;
  /** The main quest's current step for the log (title + objective), or null. */
  mainQuest(): { title: string; text: string } | null;
}

interface Bound {
  session: Session;
  mapId: string;
  heroId: UnitId;
  npcs: readonly FieldNpcDef[];
}

export class SideQuestController {
  private b: Bound | null = null;
  private tracked: string | null = null;
  private readonly barks: BarkBubbles;
  private readonly lastBark = new Map<string, number>();
  private readonly near = new Set<string>();
  private lastAnyBark = 0;
  private talkingTo: string | null = null;
  private log: TownPanel | null = null;
  private prog: { el: HTMLElement; timer: ReturnType<typeof setTimeout> | null } | null = null;

  constructor(private readonly host: SideQuestHost) {
    this.barks = new BarkBubbles(host.hudHost, () => this.b?.session.renderer ?? null);
  }

  attach(session: Session, mapId: string, heroId: UnitId, npcs: readonly FieldNpcDef[] | undefined): void {
    this.b = { session, mapId, heroId, npcs: (npcs ?? []).filter((n) => isQuestNpc(n.id)) };
    this.near.clear();
  }

  detach(): void {
    this.b = null;
    this.barks.clear();
    this.log?.close();
    this.log = null;
  }

  dispose(): void {
    this.detach();
    this.barks.dispose();
    this.prog?.el.remove();
  }

  /** This NPC's dialog is ours. */
  handles(npcId: string): boolean {
    return isQuestNpc(npcId);
  }

  // -------------------------------------------------------------------------
  // HUD model (WorldApp.refresh)
  // -------------------------------------------------------------------------

  markers(level: number): Record<string, NpcMarker | null> {
    return questNpcMarkers(this.host.store.profile.character, level);
  }

  rows(level: number): TrackerRow[] {
    const c = this.host.store.profile.character;
    if (this.tracked && !sideQuestsOf(c).active[this.tracked]) this.tracked = null;
    return sideQuestRows(c, level, this.host.names(), this.tracked);
  }

  /** The arrow: the tracked side quest, else the main quest's, else the first side quest's. */
  waypoint(level: number, main: Waypoint | null): Waypoint | null {
    const b = this.b;
    const c = this.host.store.profile.character;
    if (!b || !c) return main;
    const place = (id: string): { mapId: string; pos: Vec2 } | null => {
      const n = b.npcs.find((x) => x.id === id);
      return n ? { mapId: b.mapId, pos: n.pos } : null;
    };
    if (this.tracked) {
      const wp = sideQuestWaypoint(c, level, this.tracked, b.mapId, place, this.host.names());
      if (wp) return wp;
    }
    if (main) return main;
    const first = firstActiveQuest(c);
    return first ? sideQuestWaypoint(c, level, first, b.mapId, place, this.host.names()) : null;
  }

  /** Point the arrow at a side quest (again: back to the main quest). */
  track(id: string): void {
    this.tracked = this.tracked === id ? null : id;
    this.host.refresh();
  }

  // -------------------------------------------------------------------------
  // The quest window
  // -------------------------------------------------------------------------

  open(npc: FieldNpcDef): void {
    const voice = questNpcVoice(npc.id);
    this.talkingTo = npc.id;
    this.b?.session.renderer.npcEmote?.(npc.id);
    openQuestDialog(npc, {
      host: this.host.hudHost,
      names: this.host.names(),
      greeting: voice?.greeting ?? 'Hello there.',
      ...(voice?.done ? { doneLine: voice.done } : {}),
      menu: () => npcQuestMenu(this.host.store.profile.character, this.host.heroLevel(), npc.id),
      progressOf: (id) => sideQuestsOf(this.host.store.profile.character).active[id] ?? 0,
      accept: (id) => this.accept(id),
      turnIn: (id) => this.turnIn(id, npc.id),
      onClose: () => {
        if (this.talkingTo === npc.id) this.talkingTo = null;
      },
    });
  }

  private async accept(id: string): Promise<boolean> {
    const r = await this.host.store.act('questAccept', { questId: id });
    if (!r.ok) {
      kmToast(r.reason, 'error');
      playSound('ui.error');
      return false;
    }
    playSound('ui.chime');
    const def = sideQuestById(id);
    if (def) this.flash(`Quest accepted: ${def.title}`, false);
    this.host.refresh();
    return true;
  }

  private async turnIn(id: string, npcId: string): Promise<boolean> {
    const r = await this.host.store.act('questTurnIn', { questId: id });
    if (!r.ok) {
      kmToast(r.reason, 'error');
      playSound('ui.error');
      return false;
    }
    const g = r as unknown as FieldGain & { quest: { title: string; xp: number; gold: number; grey: boolean }; item: Item | null; equipped: boolean };
    if (this.tracked === id) this.tracked = null;
    const lines = [g.quest.xp ? `+${g.quest.xp} XP` : '', g.quest.gold ? `+${g.quest.gold} gold` : '', g.item ? `New gear: ${gearName(g.item)}${g.equipped ? ' (equipped)' : ''}` : ''].filter(Boolean);
    celebrations.current?.small({ title: 'QUEST COMPLETE', subtitle: g.quest.title, lines });
    playSound('bounty.complete');
    const b = this.b;
    if (b) {
      b.session.renderer.celebrate?.(b.heroId, 'small');
      b.session.renderer.npcEmote?.(npcId);
      const hero = b.session.sim.getWorld().units[b.heroId];
      if (hero) rewardPops.current?.add(hero.pos, [{ kind: 'xp', amount: g.quest.xp }, { kind: 'gold', amount: g.quest.gold }, ...(g.item ? [{ kind: 'item' as const, amount: 1, rarity: g.item.rarity, label: gearName(g.item) }] : [])]);
    }
    this.host.applyGain(g);
    return true;
  }

  // -------------------------------------------------------------------------
  // The quest log
  // -------------------------------------------------------------------------

  toggleLog(): void {
    if (this.log && !this.log.closed) {
      this.log.close();
      this.log = null;
      return;
    }
    closeTownPanel();
    const names = this.host.names();
    this.log = openQuestLog({
      host: this.host.hudHost,
      max: QUEST_LOG_MAX,
      main: () => this.host.mainQuest(),
      rows: () => {
        const c = this.host.store.profile.character;
        const level = this.host.heroLevel();
        return sideQuestRows(c, level, names, this.tracked).map((r) => {
          const def = sideQuestById(r.id)!;
          return {
            id: r.id,
            title: r.title,
            level: def.level,
            area: names.area(def.areaId) ?? '',
            objective: def.objective,
            progress: questProgressText(def, sideQuestsOf(c).active[r.id] ?? 0, names),
            ready: r.done || def.kind === 'talk',
            grey: !!r.grey,
            tracked: !!r.tracked,
            hint: `Return to ${names.npc(sideQuestTurnInId(def)) ?? 'the quest giver'}`,
          };
        });
      },
      track: (id) => this.track(id),
      abandon: async (id) => {
        const r = await this.host.store.act('questAbandon', { questId: id });
        if (!r.ok) kmToast(r.reason, 'error');
        else if (this.tracked === id) this.tracked = null;
        this.host.refresh();
        return r.ok;
      },
      onClose: () => (this.log = null),
    });
  }

  // -------------------------------------------------------------------------
  // Kill progress (FieldGain.quests)
  // -------------------------------------------------------------------------

  onGain(g: FieldGain): void {
    const b = this.b;
    if (!g.quests?.length) return;
    const names = this.host.names();
    const hero = b ? b.session.sim.getWorld().units[b.heroId] : undefined;
    for (const q of g.quests) {
      const def = sideQuestById(q.id);
      if (!def) continue;
      if (q.gained > 0 && hero && def.item) rewardPops.current?.add(hero.pos, [{ kind: 'item', amount: q.gained, rarity: 'common', label: q.gained > 1 ? `${q.gained} ${def.item.plural}` : def.item.name }]);
      const done = q.progress >= q.count;
      this.flash(done ? `${def.title}: complete!` : questProgressText(def, q.progress, names), done);
      if (done) {
        playSound('ui.chime');
        kmToast(`${def.title}: return to ${names.npc(sideQuestTurnInId(def)) ?? 'the quest giver'}.`, 'info', 3200);
      }
    }
  }

  /** "Damp Socks: 3/5" in the middle of the screen for a moment (WoW's objective text). */
  private flash(text: string, done: boolean): void {
    if (!this.prog) this.prog = { el: el('div', 'fx-qprog', this.host.hudHost), timer: null };
    const p = this.prog;
    p.el.textContent = text;
    p.el.classList.toggle('done', done);
    p.el.classList.add('on');
    if (p.timer) clearTimeout(p.timer);
    p.timer = setTimeout(() => p.el.classList.remove('on'), 2200);
  }

  // -------------------------------------------------------------------------
  // Barks
  // -------------------------------------------------------------------------

  /** Upkeep: a quest NPC the hero just walked up to may call out a line. */
  upkeep(heroPos: Vec2 | null): void {
    const b = this.b;
    if (!b || !heroPos) return;
    const now = performance.now();
    for (const n of b.npcs) {
      const d = Math.hypot(n.pos.x - heroPos.x, n.pos.y - heroPos.y);
      const inRange = d <= BARK_YD;
      const was = this.near.has(n.id);
      if (!inRange) {
        if (was) this.near.delete(n.id);
        continue;
      }
      if (was) continue;
      this.near.add(n.id);
      if (this.talkingTo === n.id || townPanelOpen()) continue;
      if (now - (this.lastBark.get(n.id) ?? -Infinity) < BARK_COOLDOWN_MS || now - this.lastAnyBark < BARK_GAP_MS) continue;
      const lines = questNpcVoice(n.id)?.barks ?? [];
      if (!lines.length) continue;
      const line = lines[Math.floor(Math.random() * lines.length)]!;
      this.bark(n, line);
    }
  }

  /** Show a bark now (also the debug hook). */
  bark(n: FieldNpcDef, line: string): void {
    const now = performance.now();
    this.lastBark.set(n.id, now);
    this.lastAnyBark = now;
    this.barks.show(n.id, line, n.pos, BARK_HEIGHT);
    this.b?.session.renderer.npcEmote?.(n.id);
  }

  /** Debug: bark the nearest quest NPC. */
  debugBark(heroPos: Vec2): void {
    const b = this.b;
    if (!b?.npcs.length) return;
    const n = [...b.npcs].sort((x, y) => Math.hypot(x.pos.x - heroPos.x, x.pos.y - heroPos.y) - Math.hypot(y.pos.x - heroPos.x, y.pos.y - heroPos.y))[0]!;
    this.bark(n, questNpcVoice(n.id)?.barks[0] ?? '...');
  }
}

/** "Uncommon Helm (ilvl 112)". */
function gearName(it: Item): string {
  const cap = (t: string): string => t.charAt(0).toUpperCase() + t.slice(1);
  return `${cap(it.rarity)} ${cap(it.slot)} (ilvl ${it.ilvl})`;
}
